import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { BRAND_FONT } from '@/components/brand/den-logo';

/* -------------------------------------------------------------------------- */
/*  "Book a demo" dialog with an embedded Calendly scheduler.                  */
/*                                                                            */
/*  Mirrors the marketing site's demo popup: a raw Calendly inline widget      */
/*  (Calendly injects the iframe) themed to Den's colors. Loaded on demand     */
/*  when the dialog first opens, so the sign-in page pays nothing for it up    */
/*  front. Edit the embed URL / theme in one place here.                       */
/* -------------------------------------------------------------------------- */

const CALENDLY_URL =
  'https://calendly.com/onchainden/demo?hide_event_type_details=1&hide_gdpr_banner=1&text_color=0f172a&primary_color=0f172a';

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget(options: {
        url: string;
        parentElement: HTMLElement;
      }): void;
    };
  }
}

/** Load the Calendly widget script once (shared, idempotent). */
let calendlyScriptPromise: Promise<void> | null = null;
function loadCalendly(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Calendly) return Promise.resolve();
  if (calendlyScriptPromise) return calendlyScriptPromise;
  calendlyScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      calendlyScriptPromise = null; // allow a retry
      script.remove(); // don't leave the failed tag in <head> to accumulate
      reject(new Error('Failed to load the Calendly widget.'));
    };
    document.head.appendChild(script);
  });
  return calendlyScriptPromise;
}

type BookDemoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function BookDemoDialog({ open, onOpenChange }: BookDemoDialogProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  // Render the inline widget into the host each time the dialog opens.
  // initInlineWidget injects a fresh iframe, so clear the host first.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setReady(false);

    loadCalendly()
      .then(() => {
        const el = hostRef.current;
        if (cancelled || !el || !window.Calendly) return;
        el.innerHTML = '';
        window.Calendly.initInlineWidget({
          url: CALENDLY_URL,
          parentElement: el,
        });
        el.querySelector('iframe')?.addEventListener('load', () => {
          if (!cancelled) setReady(true);
        });
      })
      .catch(() => {
        if (!cancelled) setReady(true); // stop the spinner; show empty slot
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Calendly posts this once the scheduler is interactive.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Only trust messages actually sent by the embedded Calendly widget:
      // any script on the page can postMessage, so validate the origin before
      // acting on the payload.
      if (
        e.origin === 'https://calendly.com' &&
        e.data &&
        typeof e.data === 'object' &&
        e.data.event === 'calendly.event_type_viewed'
      ) {
        setReady(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 sm:rounded-2xl"
        style={{ fontFamily: BRAND_FONT }}
      >
        {/* Centered title that shrinks + softens once the scheduler is ready,
            so it recedes behind the widget (mirrors the marketing site). */}
        <DialogHeader className="space-y-0">
          <DialogTitle
            className={cn(
              'text-center transition-all duration-300',
              ready
                ? 'text-base font-medium text-muted-foreground'
                : 'text-lg font-semibold text-foreground'
            )}
          >
            Schedule a demo
          </DialogTitle>
        </DialogHeader>

        {/* White scheduler panel; spinner overlays until the iframe is ready. */}
        <div className="relative mt-4 overflow-hidden rounded-3xl border bg-white">
          <div
            ref={hostRef}
            style={{ minWidth: 320, height: 'min(600px, 72vh)' }}
          />
          {!ready && (
            <div className="absolute inset-0 grid place-items-center bg-white">
              <span
                role="status"
                aria-label="Loading scheduler"
                className="size-7 animate-spin rounded-full border-2 border-muted border-t-foreground"
              />
            </div>
          )}
        </div>

        {/* Prefer email? Reach sales directly. */}
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Prefer email? Reach us at{' '}
          <a
            href="mailto:sales@onchainden.com"
            className="font-medium text-foreground underline-offset-4 hover:underline hover:text-zinc-500"
          >
            sales@onchainden.com
          </a>
        </p>
      </DialogContent>
    </Dialog>
  );
}
